import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';

interface JobListing {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Freelance' | 'Internship';
  experience: string;
  description: string;
  requirements: string[];
  postedDate: string;
  applicationDeadline: string;
  remote: boolean;
  verified: boolean;
  applications: number;
}

const jobTypes = ['All Types', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
const experienceLevels = ['All Levels', 'Entry Level', '1-3 years', '3-5 years', '5+ years', 'Senior Level'];
const locations = ['All Locations', 'Lagos', 'Abuja', 'Port Harcourt', 'Kano', 'Ibadan', 'Kaduna', 'Benin City', 'Remote'];

const mockJobs: JobListing[] = [
  {
    id: '1',
    title: 'Senior Software Engineer',
    company: 'TechVision Nigeria',
    location: 'Lagos',
    salary: '₦2,500,000 - ₦3,500,000',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'We are seeking a talented Senior Software Engineer to join our dynamic team. You will be responsible for developing scalable web applications and leading technical initiatives.',
    requirements: ['Bachelor\'s degree in Computer Science', '5+ years of JavaScript experience', 'React/Node.js expertise', 'Team leadership skills'],
    postedDate: '2024-01-15',
    applicationDeadline: '2024-02-15',
    remote: false,
    verified: true,
    applications: 45
  },
  {
    id: '2',
    title: 'Digital Marketing Manager',
    company: 'Brand Masters',
    location: 'Abuja',
    salary: '₦1,800,000 - ₦2,200,000',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Lead our digital marketing efforts across multiple channels. Develop and execute comprehensive marketing strategies to drive brand awareness and customer acquisition.',
    requirements: ['Digital marketing certification', 'Google Ads & Analytics experience', 'Social media expertise', 'Content creation skills'],
    postedDate: '2024-01-12',
    applicationDeadline: '2024-02-10',
    remote: true,
    verified: true,
    applications: 32
  },
  {
    id: '3',
    title: 'Sales Representative',
    company: 'Nigeria Sales Corp',
    location: 'Port Harcourt',
    salary: '₦800,000 - ₦1,200,000',
    type: 'Full-time',
    experience: '1-3 years',
    description: 'Join our sales team to drive revenue growth in the Port Harcourt region. Build relationships with clients and achieve monthly sales targets.',
    requirements: ['Sales experience preferred', 'Excellent communication skills', 'Customer service focus', 'Valid driver\'s license'],
    postedDate: '2024-01-10',
    applicationDeadline: '2024-02-08',
    remote: false,
    verified: false,
    applications: 18
  },
  {
    id: '4',
    title: 'Graphic Designer',
    company: 'Creative Studios Lagos',
    location: 'Lagos',
    salary: '₦600,000 - ₦900,000',
    type: 'Contract',
    experience: '1-3 years',
    description: 'Create visually compelling designs for various digital and print media. Work on branding projects, marketing materials, and web graphics.',
    requirements: ['Adobe Creative Suite proficiency', 'Portfolio of design work', 'Brand identity experience', 'Typography skills'],
    postedDate: '2024-01-08',
    applicationDeadline: '2024-02-05',
    remote: true,
    verified: true,
    applications: 67
  },
  {
    id: '5',
    title: 'Data Analyst',
    company: 'Analytics Pro Nigeria',
    location: 'Ibadan',
    salary: '₦1,400,000 - ₦1,800,000',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Analyze complex datasets to provide actionable insights for business decisions. Create reports and dashboards for various departments.',
    requirements: ['Statistics or Mathematics degree', 'Python/R programming', 'SQL expertise', 'Data visualization skills'],
    postedDate: '2024-01-05',
    applicationDeadline: '2024-02-02',
    remote: false,
    verified: true,
    applications: 28
  },
  {
    id: '6',
    title: 'Customer Service Agent',
    company: 'Service Excellence Ltd',
    location: 'Kano',
    salary: '₦450,000 - ₦650,000',
    type: 'Part-time',
    experience: 'Entry Level',
    description: 'Provide exceptional customer support via phone, email, and chat. Resolve customer inquiries and maintain high satisfaction ratings.',
    requirements: ['High school certificate', 'English fluency', 'Computer literacy', 'Patience and empathy'],
    postedDate: '2024-01-03',
    applicationDeadline: '2024-01-30',
    remote: true,
    verified: false,
    applications: 89
  },
  {
    id: '7',
    title: 'Content Writer',
    company: 'WordCraft Media',
    location: 'Remote',
    salary: '₦800,000 - ₦1,100,000',
    type: 'Freelance',
    experience: '1-3 years',
    description: 'Create engaging content for websites, blogs, and social media. Research topics and write SEO-optimized articles for various industries.',
    requirements: ['Excellent writing skills', 'SEO knowledge', 'Research abilities', 'Content management systems'],
    postedDate: '2024-01-01',
    applicationDeadline: '2024-01-28',
    remote: true,
    verified: true,
    applications: 54
  },
  {
    id: '8',
    title: 'Project Manager',
    company: 'BuildRight Construction',
    location: 'Kaduna',
    salary: '₦2,000,000 - ₦2,800,000',
    type: 'Full-time',
    experience: '5+ years',
    description: 'Oversee construction projects from planning to completion. Manage timelines, budgets, and coordinate with various stakeholders.',
    requirements: ['Project management certification', 'Construction industry experience', 'Leadership skills', 'Budget management'],
    postedDate: '2023-12-28',
    applicationDeadline: '2024-01-25',
    remote: false,
    verified: true,
    applications: 23
  }
];

export default function JobsPage() {
  const [jobs, setJobs] = useState<JobListing[]>(mockJobs);
  const [filteredJobs, setFilteredJobs] = useState<JobListing[]>(mockJobs);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('All Types');
  const [selectedExperience, setSelectedExperience] = useState('All Levels');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [showRemoteOnly, setShowRemoteOnly] = useState(false);
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    let filtered = jobs;

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Job type filter
    if (selectedJobType !== 'All Types') {
      filtered = filtered.filter(job => job.type === selectedJobType);
    }

    // Experience filter
    if (selectedExperience !== 'All Levels') {
      filtered = filtered.filter(job => job.experience === selectedExperience);
    }

    // Location filter
    if (selectedLocation !== 'All Locations') {
      filtered = filtered.filter(job => 
        job.location === selectedLocation || 
        (selectedLocation === 'Remote' && job.remote)
      );
    }

    // Remote filter
    if (showRemoteOnly) {
      filtered = filtered.filter(job => job.remote);
    }

    // Verified filter
    if (showVerifiedOnly) {
      filtered = filtered.filter(job => job.verified);
    }

    setFilteredJobs(filtered);
  }, [jobs, searchQuery, selectedJobType, selectedExperience, selectedLocation, showRemoteOnly, showVerifiedOnly]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Full-time': return 'bg-green-100 text-green-800';
      case 'Part-time': return 'bg-blue-100 text-blue-800';
      case 'Contract': return 'bg-orange-100 text-orange-800';
      case 'Freelance': return 'bg-purple-100 text-purple-800';
      case 'Internship': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApplyClick = (job: JobListing) => {
    setSelectedJob(job);
    setShowApplicationModal(true);
    setSubmitMessage('');
  };

  const handleSubmitApplication = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    
    // Validate required fields
    const fullName = formData.get('full_name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const coverLetter = formData.get('cover_letter') as string;
    
    if (!fullName || !email || !phone || !coverLetter) {
      setSubmitMessage('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }
    
    if (coverLetter.length > 500) {
      setSubmitMessage('Cover letter must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4erta2okpliv29boceg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });
      
      if (response.ok) {
        setSubmitMessage('Application submitted successfully! We will contact you soon.');
        setTimeout(() => {
          setShowApplicationModal(false);
          setSubmitMessage('');
        }, 3000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setSubmitMessage('Failed to submit application. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              <i className="ri-briefcase-line mr-3 text-blue-600"></i>
              Jobs in Nigeria
            </h1>
            <p className="text-gray-600 text-lg mb-6">
              Find your next career opportunity from top Nigerian employers
            </p>
            <div className="flex justify-center gap-6 text-sm text-gray-500">
              <span><strong>{filteredJobs.length}</strong> Active Jobs</span>
              <span><strong>500+</strong> Companies Hiring</span>
              <span><strong>1,200+</strong> Applications This Week</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <i className="ri-filter-line mr-2"></i>
                Filter Jobs
              </h3>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Jobs
                </label>
                <Input
                  type="text"
                  placeholder="Job title, company, keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* Job Type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type
                </label>
                <select
                  value={selectedJobType}
                  onChange={(e) => setSelectedJobType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                >
                  {jobTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Experience Level */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  value={selectedExperience}
                  onChange={(e) => setSelectedExperience(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                >
                  {experienceLevels.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                >
                  {locations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showRemoteOnly}
                    onChange={(e) => setShowRemoteOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Remote Jobs Only</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showVerifiedOnly}
                    onChange={(e) => setShowVerifiedOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Verified Employers Only</span>
                </label>
              </div>

              {/* Post Job Button */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Link to="/post-ad">
                  <Button variant="primary" className="w-full">
                    <i className="ri-add-line mr-2"></i>
                    Post a Job
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Job Listings */}
          <div className="flex-1">
            {filteredJobs.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                <p className="text-gray-500 mb-6">
                  Try adjusting your filters or search terms
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedJobType('All Types');
                    setSelectedExperience('All Levels');
                    setSelectedLocation('All Locations');
                    setShowRemoteOnly(false);
                    setShowVerifiedOnly(false);
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-product-shop>
                {filteredJobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                            {job.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(job.type)}`}>
                            {job.type}
                          </span>
                          {job.verified && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              <i className="ri-shield-check-line mr-1"></i>
                              Verified
                            </span>
                          )}
                          {job.remote && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                              <i className="ri-global-line mr-1"></i>
                              Remote
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <span className="font-medium text-gray-900">{job.company}</span>
                          <span className="flex items-center">
                            <i className="ri-map-pin-line mr-1"></i>
                            {job.location}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-time-line mr-1"></i>
                            {job.experience}
                          </span>
                        </div>
                        
                        <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                          {job.description}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {job.requirements.slice(0, 3).map((req, index) => (
                            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                              {req}
                            </span>
                          ))}
                          {job.requirements.length > 3 && (
                            <span className="text-gray-500 text-xs">
                              +{job.requirements.length - 3} more
                            </span>
                          )}
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{formatDate(job.postedDate)}</span>
                            <span>•</span>
                            <span>{job.applications} applications</span>
                            <span>•</span>
                            <span>Deadline: {new Date(job.applicationDeadline).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right ml-6 flex-shrink-0">
                        <div className="text-lg font-bold text-green-600 mb-2">
                          {job.salary}
                        </div>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="whitespace-nowrap"
                          onClick={() => handleApplyClick(job)}
                        >
                          <i className="ri-send-plane-line mr-1"></i>
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load More */}
            {filteredJobs.length > 0 && (
              <div className="text-center mt-8">
                <Button variant="outline" className="px-8">
                  Load More Jobs
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Find Your Dream Job?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join thousands of job seekers who found their perfect career match on ListIt
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signin">
              <Button variant="outline" size="lg" className="px-8 bg-white/10 border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-user-line mr-2"></i>
                Create Account
              </Button>
            </Link>
            <Link to="/post-ad">
              <Button variant="primary" size="lg" className="px-8 bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-briefcase-line mr-2"></i>
                Post a Job
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Job Application Modal */}
      {showApplicationModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Apply for Position</h2>
                  <p className="text-gray-600">{selectedJob.title} at {selectedJob.company}</p>
                </div>
                <button
                  onClick={() => setShowApplicationModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitApplication} className="p-6" data-readdy-form>
              <input type="hidden" name="job_title" value={selectedJob.title} />
              <input type="hidden" name="company" value={selectedJob.company} />
              <input type="hidden" name="job_id" value={selectedJob.id} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="full_name"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="+234 XXX XXX XXXX"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Location
                  </label>
                  <Input
                    type="text"
                    name="location"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience
                  </label>
                  <select
                    name="experience_years"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm pr-8"
                  >
                    <option value="">Select experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Salary
                  </label>
                  <Input
                    type="text"
                    name="expected_salary"
                    placeholder="₦XXX,XXX - ₦XXX,XXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn Profile
                  </label>
                  <Input
                    type="url"
                    name="linkedin"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Portfolio/Website
                  </label>
                  <Input
                    type="url"
                    name="portfolio"
                    placeholder="https://yourportfolio.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resume/CV *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <i className="ri-file-upload-line text-2xl text-gray-400 mb-2"></i>
                  <p className="text-sm text-gray-600 mb-2">Upload your resume (PDF, DOC, DOCX)</p>
                  <input
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="resume-upload"
                  />
                  <label
                    htmlFor="resume-upload"
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer"
                  >
                    <i className="ri-folder-open-line mr-2"></i>
                    Choose File
                  </label>
                  <p className="text-xs text-gray-500 mt-2">File uploads show as "Uncollectable" in submissions</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter * (Max 500 characters)
                </label>
                <textarea
                  name="cover_letter"
                  rows={6}
                  maxLength={500}
                  placeholder="Tell us why you're the perfect fit for this role..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm resize-none"
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Describe your relevant experience and why you want this job</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Skills & Technologies
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'HTML/CSS', 'Java', 'C#', 'PHP', 'Marketing', 'Sales', 'Design'].map((skill) => (
                    <label key={skill} className="flex items-center">
                      <input
                        type="checkbox"
                        name="skills"
                        value={skill}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-2"
                      />
                      <span className="text-sm text-gray-700">{skill}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="availability"
                    value="immediate"
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-2"
                  />
                  <span className="text-sm text-gray-700">Available to start immediately</span>
                </label>
              </div>

              {submitMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  submitMessage.includes('successfully') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  <i className={`mr-2 ${submitMessage.includes('successfully') ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
                  {submitMessage}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowApplicationModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
